import { useCallback, useState } from 'react';
import { feedbackUtils, sentryUtils } from '@/utils/sentry';
import { useAuthStore } from '@/stores/auth-store';
import {
  FeedbackContext,
  FeedbackContextType,
  type FeedbackContext as FeedbackRequestContext,
} from './FeedbackContextType';

export { FeedbackContext };

interface FeedbackProviderProps {
  children: React.ReactNode;
  defaultEnabled?: boolean;
}

export function FeedbackProvider({ children, defaultEnabled = true }: FeedbackProviderProps) {
  const [isEnabled, setIsEnabled] = useState(defaultEnabled);
  const { user } = useAuthStore();

  const showFeedback = useCallback(async (context?: FeedbackRequestContext) => {
    if (!isEnabled) return;

    const tags: Record<string, string> = {
      page: context?.page || window.location.pathname,
      feature: context?.feature || 'general',
      ...(context?.tags || {}),
    };

    const extra: Record<string, unknown> = {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      userAction: context?.userAction,
      errorId: context?.errorId,
      severity: context?.severity || 'medium',
      ...(context?.extra || {}),
    };

    // Persist tags/context for the dialog submission (do not auto-send blank feedback)
    Object.entries(tags).forEach(([key, value]) => sentryUtils.setTag(key, value));
    sentryUtils.setContext('feedback', extra);

    await feedbackUtils.showFeedbackDialog();
  }, [isEnabled]);

  const sendFeedback = useCallback((message: string, context?: FeedbackRequestContext) => {
    if (!isEnabled) return;

    const tags: Record<string, string> = {
      page: context?.page || window.location.pathname,
      feature: context?.feature || 'general',
      type: 'programmatic',
      ...(context?.tags || {}),
    };

    const extra: Record<string, unknown> = {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      userAction: context?.userAction,
      errorId: context?.errorId,
      severity: context?.severity || 'medium',
      ...(context?.extra || {}),
    };

    feedbackUtils.captureFeedbackWithContext(message, {
      name: user?.attributes?.name || user?.attributes?.preferred_username,
      email: user?.attributes?.email,
      tags,
      extra,
    });
  }, [isEnabled, user]);

  const setEnabled = useCallback((enabled: boolean) => {
    setIsEnabled(enabled);
  }, []);

  const value: FeedbackContextType = {
    isEnabled,
    showFeedback,
    sendFeedback,
    setEnabled,
  };

  return (
    <FeedbackContext.Provider value={value}>
      {children}
    </FeedbackContext.Provider>
  );
}
